"use client";

import { c as _c } from "react/compiler-runtime";
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BrainMobileTabs from "./mobile/BrainMobileTabs";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import BrainDesktopDrop from "./BrainDesktopDrop";
import BrainMobileAbout from "./mobile/BrainMobileAbout";
import { DropSize, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./my-stream/MyStreamWaveLeaderboard";
import MyStreamWaveOutcome from "./my-stream/MyStreamWaveOutcome";
import { WaveWinners } from "../waves/winners/WaveWinners";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import MyStreamWaveMyVotes from "./my-stream/votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./my-stream/MyStreamWaveFAQ";
import { useWave } from "@/hooks/useWave";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import BrainMobileWaves from "./mobile/BrainMobileWaves";
import BrainMobileMessages from "./mobile/BrainMobileMessages";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import BrainNotifications from "./notifications/NotificationsContainer";
import { getHomeFeedRoute } from "@/helpers/navigation.helpers";
import CreateWaveModal from "@/components/waves/create-wave/CreateWaveModal";
import CreateDirectMessageModal from "@/components/waves/create-dm/CreateDirectMessageModal";
import { useAuth } from "@/components/auth/Auth";
export enum BrainView {
  DEFAULT = "DEFAULT",
  ABOUT = "ABOUT",
  LEADERBOARD = "LEADERBOARD",
  WINNERS = "WINNERS",
  OUTCOME = "OUTCOME",
  MY_VOTES = "MY_VOTES",
  FAQ = "FAQ",
  WAVES = "WAVES",
  MESSAGES = "MESSAGES",
  NOTIFICATIONS = "NOTIFICATIONS",
}
interface Props {
  readonly children: ReactNode;
}
const BrainMobile: React.FC<Props> = t0 => {
  const $ = _c(99);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile
  } = useAuth();
  const [hydrated, setHydrated] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHydrated(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const [activeView, setActiveView] = useState(BrainView.DEFAULT);
  const dropId = searchParams?.get("drop") ?? undefined;
  const {
    data: drop
  } = useQuery({
    queryKey: [QueryKey.DROP, {
      drop_id: dropId
    }],
    queryFn: async () => await commonApiFetch({
      endpoint: `drops/${dropId}`
    }),
    placeholderData: keepPreviousData,
    enabled: !!dropId
  });
  let t3;
  if ($[2] !== searchParams) {
    t3 = searchParams?.get("wave") ?? null;
    $[2] = searchParams;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const waveId = t3;
  let t4;
  if ($[4] !== pathname || $[5] !== router || $[6] !== searchParams) {
    t4 = () => {
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.delete("wave");
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeFeedRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[4] = pathname;
    $[5] = router;
    $[6] = searchParams;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4 || $[9] !== waveId) {
    t5 = {
      waveId,
      onWaveNotFound: t4
    };
    $[8] = t4;
    $[9] = waveId;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    data: wave
  } = useWaveData(t5);
  const {
    isMemesWave
  } = useWave(wave);
  const {
    voting: t6,
    decisions: t7
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t6;
  const {
    firstDecisionDone
  } = t7;
  let t8;
  if ($[11] !== pathname || $[12] !== router || $[13] !== searchParams) {
    t8 = drop_0 => {
      const params_0 = new URLSearchParams(searchParams?.toString() || "");
      params_0.set("drop", drop_0.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[11] = pathname;
    $[12] = router;
    $[13] = searchParams;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const onDropClick = t8;
  let t9;
  if ($[15] !== pathname || $[16] !== router || $[17] !== searchParams) {
    t9 = () => {
      const params_1 = new URLSearchParams(searchParams?.toString() || "");
      params_1.delete("drop");
      const newUrl_0 = params_1.toString() ? `${pathname}?${params_1.toString()}` : pathname || getHomeFeedRoute();
      router.push(newUrl_0, {
        scroll: false
      });
    };
    $[15] = pathname;
    $[16] = router;
    $[17] = searchParams;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const onDropClose = t9;
  const isDropOpen = drop && drop?.id?.toLowerCase() === dropId?.toLowerCase();
  const isRankWave = wave?.wave.type === ApiWaveType.Rank;
  const hasWave = Boolean(waveId);
  let t10;
  let t11;
  if ($[19] !== isApp || $[20] !== pathname || $[21] !== searchParams || $[22] !== waveId) {
    t10 = () => {
      const tabParam = searchParams?.get("tab");
      const viewParam = searchParams?.get("view");
      const createParam = searchParams?.get("create");
      if (createParam && isApp) {
        setActiveView(BrainView.DEFAULT);
        return;
      }
      if (!waveId && pathname === "/notifications" || !waveId && viewParam === "notifications") {
        setActiveView(BrainView.NOTIFICATIONS);
        return;
      }
      if (!waveId && pathname === "/messages" || !waveId && viewParam === "messages") {
        setActiveView(BrainView.MESSAGES);
        return;
      }
      if (!waveId && pathname === "/waves" || !waveId && viewParam === "waves") {
        setActiveView(BrainView.WAVES);
        return;
      }
      if (pathname === "/" && (!tabParam || tabParam === "feed") && !waveId && !viewParam) {
        setActiveView(BrainView.DEFAULT);
      }
    };
    t11 = [pathname, searchParams, waveId, isApp];
    $[19] = isApp;
    $[20] = pathname;
    $[21] = searchParams;
    $[22] = waveId;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[25] !== activeView || $[26] !== firstDecisionDone || $[27] !== hasWave || $[28] !== isCompleted || $[29] !== isMemesWave || $[30] !== wave || $[31] !== waveId) {
    t12 = () => {
      if (!hasWave) {
        if (activeView !== BrainView.DEFAULT && activeView !== BrainView.WAVES && activeView !== BrainView.MESSAGES && activeView !== BrainView.NOTIFICATIONS) {
          setActiveView(BrainView.DEFAULT);
        }
        return;
      }
      if (!wave) {
        return;
      }
      const isInLeaderboardAndVotingHasEnded = activeView === BrainView.LEADERBOARD && isCompleted;
      const isInWinnersAndFirstDecisionHasntPassed = activeView === BrainView.WINNERS && !firstDecisionDone;
      const isInMyVotesAndIsNotMemeWave = activeView === BrainView.MY_VOTES && !isMemesWave;
      const isInFAQAndIsNotMemeWave = activeView === BrainView.FAQ && !isMemesWave;
      if (isInLeaderboardAndVotingHasEnded || isInWinnersAndFirstDecisionHasntPassed || isInMyVotesAndIsNotMemeWave || isInFAQAndIsNotMemeWave) {
        setActiveView(BrainView.DEFAULT);
      }
      if (hasWave && waveId) {
        if (activeView === BrainView.NOTIFICATIONS || activeView === BrainView.MESSAGES || activeView === BrainView.WAVES) {
          setActiveView(BrainView.DEFAULT);
        }
      }
    };
    t13 = [hasWave, wave, isCompleted, firstDecisionDone, activeView, isMemesWave, waveId];
    $[25] = activeView;
    $[26] = firstDecisionDone;
    $[27] = hasWave;
    $[28] = isCompleted;
    $[29] = isMemesWave;
    $[30] = wave;
    $[31] = waveId;
    $[32] = t12;
    $[33] = t13;
  } else {
    t12 = $[32];
    t13 = $[33];
  }
  useEffect(t12, t13);
  let t14;
  if ($[34] !== pathname || $[35] !== router || $[36] !== searchParams) {
    t14 = () => {
      const params_2 = new URLSearchParams(searchParams?.toString() || "");
      params_2.delete("create");
      const base = pathname ?? "/";
      const next = params_2.toString() ? `${base}?${params_2.toString()}` : base;
      router.replace(next, {
        scroll: false
      });
    };
    $[34] = pathname;
    $[35] = router;
    $[36] = searchParams;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const closeCreateOverlay = t14;
  let t15;
  bb0: {
    if (!isApp) {
      t15 = null;
      break bb0;
    }
    const createParam_0 = searchParams?.get("create");
    if (!createParam_0) {
      t15 = null;
      break bb0;
    }
    if (!connectedProfile) {
      t15 = null;
      break bb0;
    }
    if (createParam_0 === "dm") {
      let t16;
      if ($[38] !== closeCreateOverlay || $[39] !== connectedProfile) {
        t16 = <CreateDirectMessageModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[38] = closeCreateOverlay;
        $[39] = connectedProfile;
        $[40] = t16;
      } else {
        t16 = $[40];
      }
      t15 = t16;
      break bb0;
    }
    if (createParam_0 === "wave") {
      let t16;
      if ($[41] !== closeCreateOverlay || $[42] !== connectedProfile) {
        t16 = <CreateWaveModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[41] = closeCreateOverlay;
        $[42] = connectedProfile;
        $[43] = t16;
      } else {
        t16 = $[43];
      }
      t15 = t16;
      break bb0;
    }
    t15 = null;
  }
  const createOverlay = t15;
  let t16;
  if ($[44] !== waveId) {
    t16 = <BrainMobileAbout activeWaveId={waveId} />;
    $[44] = waveId;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== isRankWave || $[47] !== onDropClick || $[48] !== wave) {
    t17 = isRankWave && !!wave ? <MyStreamWaveLeaderboard wave={wave} onDropClick={onDropClick} /> : null;
    $[46] = isRankWave;
    $[47] = onDropClick;
    $[48] = wave;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== isRankWave || $[51] !== onDropClick || $[52] !== wave) {
    t18 = isRankWave && !!wave ? <div className="tw-px-2 sm:tw-px-4"><WaveWinners wave={wave} onDropClick={onDropClick} /></div> : null;
    $[50] = isRankWave;
    $[51] = onDropClick;
    $[52] = wave;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== isRankWave || $[55] !== wave) {
    t19 = isRankWave && !!wave ? <MyStreamWaveOutcome wave={wave} /> : null;
    $[54] = isRankWave;
    $[55] = wave;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== isRankWave || $[58] !== onDropClick || $[59] !== wave) {
    t20 = isRankWave && !!wave ? <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} /> : null;
    $[57] = isRankWave;
    $[58] = onDropClick;
    $[59] = wave;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== isMemesWave || $[62] !== isRankWave || $[63] !== wave) {
    t21 = isRankWave && isMemesWave && !!wave ? <MyStreamWaveFAQ wave={wave} /> : null;
    $[61] = isMemesWave;
    $[62] = isRankWave;
    $[63] = wave;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <BrainMobileWaves />;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <BrainMobileMessages />;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <BrainNotifications />;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== children || $[69] !== t16 || $[70] !== t17 || $[71] !== t18 || $[72] !== t19 || $[73] !== t20 || $[74] !== t21) {
    t25 = {
      [BrainView.ABOUT]: t16,
      [BrainView.DEFAULT]: children,
      [BrainView.LEADERBOARD]: t17,
      [BrainView.WINNERS]: t18,
      [BrainView.OUTCOME]: t19,
      [BrainView.MY_VOTES]: t20,
      [BrainView.FAQ]: t21,
      [BrainView.WAVES]: t22,
      [BrainView.MESSAGES]: t23,
      [BrainView.NOTIFICATIONS]: t24
    };
    $[68] = children;
    $[69] = t16;
    $[70] = t17;
    $[71] = t18;
    $[72] = t19;
    $[73] = t20;
    $[74] = t21;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  const viewComponents = t25;
  const dropOverlayClass = isApp ? "tw-fixed tw-inset-0 tw-z-[2000] tw-bg-black tailwind-scope" : "tw-absolute tw-inset-0 tw-z-[1000]";
  let t26;
  if ($[76] !== drop || $[77] !== dropOverlayClass || $[78] !== isDropOpen || $[79] !== onDropClose) {
    t26 = isDropOpen && <div className={dropOverlayClass}><BrainDesktopDrop drop={{
        type: DropSize.FULL,
        ...drop,
        stableKey: drop.id,
        stableHash: drop.id
      }} onClose={onDropClose} /></div>;
    $[76] = drop;
    $[77] = dropOverlayClass;
    $[78] = isDropOpen;
    $[79] = onDropClose;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== activeView || $[82] !== hasWave || $[83] !== hydrated || $[84] !== isApp || $[85] !== wave) {
    t27 = (hasWave || !isApp) && <BrainMobileTabs activeView={activeView} onViewChange={setActiveView} wave={wave} waveActive={hasWave} showWavesTab={hydrated} showStreamBack={hydrated} isApp={isApp} />;
    $[81] = activeView;
    $[82] = hasWave;
    $[83] = hydrated;
    $[84] = isApp;
    $[85] = wave;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  let t28;
  let t29;
  let t30;
  let t31;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      opacity: 0,
      x: 20
    };
    t29 = {
      opacity: 1,
      x: 0
    };
    t30 = {
      opacity: 0,
      x: -20
    };
    t31 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[87] = t28;
    $[88] = t29;
    $[89] = t30;
    $[90] = t31;
  } else {
    t28 = $[87];
    t29 = $[88];
    t30 = $[89];
    t31 = $[90];
  }
  const t32 = viewComponents[activeView];
  let t33;
  if ($[91] !== activeView || $[92] !== t32) {
    t33 = <AnimatePresence mode="wait"><motion.div key={activeView} initial={t28} animate={t29} exit={t30} transition={t31} className="tw-flex-1">{t32}</motion.div></AnimatePresence>;
    $[91] = activeView;
    $[92] = t32;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== createOverlay || $[95] !== t26 || $[96] !== t27 || $[97] !== t33) {
    t34 = <div className="tw-relative tw-flex tw-flex-col tw-h-full">{createOverlay}{t26}{t27}{t33}</div>;
    $[94] = createOverlay;
    $[95] = t26;
    $[96] = t27;
    $[97] = t33;
    $[98] = t34;
  } else {
    t34 = $[98];
  }
  return t34;
};
export default BrainMobile;